/**
 * @file Base service that encapsulates the backend api by means of the breeze.js library.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { EventEmitter } from '@angular/core';
import { config, EntityState, EntityQuery, SaveOptions } from 'breeze-client';
import { AsyncSubject } from 'rxjs';
import { SortDirection } from './helpers/sort-direction';
import { DataContext } from './helpers/data-context';
import { AbstractBase } from '../core/helpers/abstract-base';
/** Name of flag property on entity to mark entity as dirty.  */
export var DIRTY_FLAG = '@dirty';
export var INVOKER_AFTER_SAVE = '@invokeAfterSave';
export var INVOKER_BEFORE_SAVE = '@invokeBeforeSave';
/** Label to alert user of loading process. */
export var LOADING_ALERT = 'APP.DOMAIN.STILL_LOADING';
/**
 * Encapsulation of breeze.js, this class manage communication with a backend.
 */
var BreezeService = /** @class */ (function (_super) {
    tslib_1.__extends(BreezeService, _super);
    /**
     * Constructs a DomainService.
     * @param alertsService The Alert service to communicate with user.
     * @memberOf BreezeService
     */
    function BreezeService(alertsService, translateService, entityManagerService) {
        var _this = _super.call(this) || this;
        _this.alertsService = alertsService;
        _this.translateService = translateService;
        _this.entityManagerService = entityManagerService;
        /**
         * An array with DataContext obects.
         * @private
         * @type {DataContext[]}
         */
        _this.dataContextSet = [];
        /**
         * Counter of loading requests in process.
         * @private
         * @memberOf BreezeService
         */
        _this._isLoading = 0;
        /**
         * Fired when the breeze service is done executing a commit.
         * @memberOf BreezeService
         */
        _this.commitFinishedEvent = new EventEmitter();
        /**
         * Fired when the breeze service is done executing a query.
         * @memberOf BreezeService
         */
        _this.queryFinishedEvent = new EventEmitter();
        /**
         * @private
         * @type {string}
         * @memberOf BreezeService
         */
        _this._dataUri = null;
        /**
         * Is used when EntityManager is not loaded yet.
         * @memberOf BreezeService
         */
        _this.loading = null;
        /**
         * Is used to solve concurrent saves.
         * @memberOf BreezeService
         */
        _this._savingInProgress = null;
        _this.loading = new AsyncSubject();
        return _this;
    }
    Object.defineProperty(BreezeService.prototype, "isLoading", {
        /**
         * Gets wether or not there a request is currently loading.
         * @readonly
         * @type {boolean}
         * @memberOf BreezeService
         */
        get: function () {
            return this._isLoading > 0;
        },
        enumerable: true,
        configurable: true
    });
    /** Gets the dynamic query parameter.
     * @param options The configured query options.
     * @param dynamicParameters The parameter values.
     * @memberOf BreezeService
     */
    BreezeService.getDynamicParams = function (options, dynamicParameters) {
        if (options.parameters) {
            var VALUE_PROPERTY_1 = 'valueProperty';
            Object.keys(options.parameters).forEach(function (paramName) {
                var value = options.parameters[paramName];
                if (typeof value === 'object' && value[VALUE_PROPERTY_1]) {
                    value.parameter = paramName;
                    dynamicParameters.push(value);
                }
            });
        }
    };
    /**
     * Call this method to invoke a remote/backend method on the entity.
     * The remote method will be invoked when changes are saved.
     * @static
     * @param {Entity} entity The entity with the remote method.
     * @param {string} name The name of the remote method.
     * @param {[]} parameters An array of parameter values. An ampty array is allowed.
     * @param {boolean} [afterSave=false] Indicating whether or not the method is invoked after properties changes are committed or before.
     * @see Example: Deployment.Plan()
     * @memberOf BreezeService
     */
    BreezeService.invokeMethod = function (entity, name, parameters, afterSave) {
        if (afterSave === void 0) { afterSave = false; }
        var result = new EventEmitter();
        if (BreezeService.invoke(result, entity, name, parameters, afterSave)) {
            window.setTimeout(function () {
                result.emit(entity);
            }, 250);
            return result;
        }
        return result;
    };
    /**
     * Is used to invoke a remote/backend method on the entity.
     * We need this method to wait on a previous invocation.
     * @private
     * @static
     * @param {EventEmitter<Entity>} process We wait on.
     * @param {Entity} entity The entity concerned.
     * @param {string} name The name of the method.
     * @param {any[]} parameters The parameters of the method.
     * @param {boolean} [afterSave=false] If we want to invoke after committing other changes.
     * @returns {Boolean} True when we do not have to wait for previous invocation.
     * @memberOf BreezeService
     * */
    BreezeService.invoke = function (process, entity, name, parameters, afterSave) {
        if (afterSave === void 0) { afterSave = false; }
        var beforeOrAfter = INVOKER_BEFORE_SAVE;
        if (afterSave) {
            beforeOrAfter = INVOKER_AFTER_SAVE;
        }
        var params = parameters;
        if (!params) {
            params = [];
        }
        var method = { 'parameters': params };
        var invokeMethods = entity[beforeOrAfter];
        if (invokeMethods == null) {
            invokeMethods = {};
            invokeMethods[name] = method;
            entity[beforeOrAfter] = invokeMethods;
            if (!entity.entityAspect.entityState.isAddedModifiedOrDeleted()) {
                entity.entityAspect.setModified();
            }
            return true;
        }
        else if (invokeMethods[name] == null) {
            invokeMethods[name] = method;
            if (!entity.entityAspect.entityState.isAddedModifiedOrDeleted()) {
                entity.entityAspect.setModified();
            }
            return true;
        }
        var test = invokeMethods[name]['parameters'];
        if (parameters) {
            if (test.length === parameters.length && parameters.filter(function (value, index) { return test[index] !== value; }).length === 0) {
                // Geen verschil gevonden. Dus te snel achter elkaar aangeroepen.
                return true;
            }
        }
        else if (test.length === 0) {
            // Geen verschil gevonden. Dus te snel achter elkaar aangeroepen.
            return true;
        }
        var subscription = invokeMethods[name]['process'].subscribe(function (value) {
            subscription.unsubscribe();
            BreezeService.invoke(process, entity, name, parameters, afterSave);
        });
        method['process'] = process;
        return false;
    };
    /** Sets the dynamic query parameter.
     * @param options The requested query options.
     * @param dynamicParameters The parameter values.
     * @param component The component has query properties as its own properties.
     * @return True when a dynamic parameter value has changed.
     * @memberOf BreezeService
     */
    BreezeService.setDynamicParams = function (options, dynamicParameters, component) {
        var changed = false;
        dynamicParameters.forEach(function (item) {
            var previous = options.parameters[item.parameter];
            var path = item.valueProperty.split('.');
            var value = component;
            while (path.length > 0 && value) {
                value = value[path.shift()];
            }
            if (value == null) {
                options.parameters[item.parameter] = '';
                changed = true;
                return;
            }
            if (value != null && previous !== value) {
                options.parameters[item.parameter] = value;
                changed = true;
                return;
            }
        });
        return changed;
    };
    Object.defineProperty(BreezeService.prototype, "savingInProgress", {
        /**
         * Gets, when buzy, the saving in progress otherwise returns null.
         * @readonly
         * @type {Observable<SaveResult>}
         * @memberOf BreezeService
         */
        get: function () {
            return this._savingInProgress;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get the value of a entity according the specified path.
     * @param path Point separated property path.
     * @param entity The entity.
     * @memberOf BreezeService
     */
    BreezeService.valueOf = function (path, entity) {
        if (!entity) {
            return undefined;
        }
        var result = entity;
        var segments = path.split('.');
        segments.find(function (segment) {
            result = result[segment];
            return !result;
        });
        return result;
    };
    /**
     * Configures the ajax communication of breeze for angular.
     * @param oauthService The OAuth authentication service.
     * @memberOf BreezeService
     */
    BreezeService.adapterSetup = function (translateService, alertsService) {
        // TODO: For performance consider using: oauthService.tryLogin({ onTokenReceived: context => { /* Set Bearer Token */}})
        // see: https://github.com/manfredsteyer/angular2-oauth2
        // see also: AppComponent
        var adapter = config.getAdapterInstance('ajax', 'angular');
        adapter.requestInterceptor = function (requestInfo) {
            var errorHandler = requestInfo.error;
            requestInfo.error = function (response) {
                var errors = null;
                var error = null;
                try {
                    error = JSON.parse(response.error);
                    errors = error['EntityErrors'];
                }
                catch (_a) {
                    // Did zou niet kunnen voorkomen zie: ErrorInterceptor
                    throw new Error(response.error);
                }
                if (errors && errors.length > 0) {
                    errorHandler(response);
                    return;
                }
                if (error) {
                    var label_1 = error['label'];
                    if (label_1) {
                        translateService.get(label_1).subscribe(function (value) {
                            var message = error['message'];
                            if (value !== label_1) {
                                // If we have a translation
                                alertsService.createAlert('danger', value, message);
                            }
                            else {
                                // We don't have a translation, use a fallback (#3522)
                                alertsService.createAlert('danger', 'APP.CORE.GENERAL_SERVER_ERROR', error['Message'] || error);
                            }
                        }).unsubscribe();
                    }
                    else {
                        // We don't have a translation, use a fallback (#3522)
                        alertsService.createAlert('danger', 'APP.CORE.GENERAL_SERVER_ERROR', error['Message'] || error);
                    }
                }
                errorHandler(response);
            };
        };
    };
    /**
     * Sets the entity manager and metadata.
     * @param {string} domain The name of the domain.
     * @memberOf BreezeService
     */
    BreezeService.prototype.initialize = function (domain) {
        this.entityManager = this.entityManagerService.managers[domain];
        this.metadataStore = this.entityManagerService.metadatas[domain];
    };
    /**
     * Adds a new data context and makes it the current data context.
     */
    BreezeService.prototype.nextDataContext = function () {
        if (!this.entityManager) {
            return;
        }
        this.dataContextSet.push(new DataContext(this.entityManager));
    };
    /**
     * Removes the current data context.
     */
    BreezeService.prototype.previousDataContext = function () {
        this.dataContextSet.pop();
    };
    /**
     *
     * @param {string} method
     * @returns {string}
     * @memberOf BreezeService
     */
    BreezeService.prototype.uriForMethod = function (method) {
        return this._dataUri + method;
    };
    /**
     * Registers an entity data source of a DataComponent.
     * @param entityType Name of entity type.
     * @param component The DataComponent.
     * @memberOf BreezeService
     */
    BreezeService.prototype.registerSource = function (entityType, component) {
        var resultType = this.getEntityType(entityType);
        component.entityType = resultType;
        return component;
    };
    /**
     * Registers an entity data source of a DataComponent and loads the associated data source with entities.
     * @param entityType Name of the entity type.
     * @param component The DataComponent.
     * @param dataSource The data source to load and fill.
     * @param loadData Indicates whether the data set should be loaded consequently. (#1820 and #1710)
     * @memberOf BreezeService
     */
    BreezeService.prototype.registerAndLoadSource = function (entityType, component, dataSource) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var completer, registered;
            return tslib_1.__generator(this, function (_a) {
                completer = new AsyncSubject();
                registered = this.registerSource(entityType, component);
                this.loadDataSet(this.entityManager, completer, registered, dataSource);
                return [2 /*return*/, completer.toPromise()];
            });
        });
    };
    /**
     * Converts EntityError array into string array so one can display the information.
     * @param {EntityError[]} errors The array to convert.
     * @returns {string[]} The string array
     * @memberOf BreezeService
     */
    BreezeService.prototype.entityErrorInfo = function (errors) {
        if (!errors) {
            return undefined;
        }
        var result = [];
        errors.forEach(function (item) {
            var info = 'Error: ' + item.errorMessage ? item.errorMessage : item.errorName +
                ' ' + item.entity.entityType.shortName + '.' + item.propertyName;
            result.push(info);
        });
        return result;
    };
    /**
     * Sets default values for every newly created entity (just after creation).
     * Specially for standard entity types.
     * @param item The entity to update.
     * @memberOf BreezeService
     */
    BreezeService.prototype.setDefaultValues = function (user, item) {
        item.entityType.dataProperties.forEach(function (property) {
            if (property.dataType.isDate) {
                // To begin with a valid date value.
                item[property.name] = new Date();
                // Set seconds and milliseconds to zero, otherwise this will trigger an unwanted
                // propertyChanged event on the entity later on, that sets the DIRTY_FLAG. See issue #980.
                item[property.name].setSeconds(0, 0);
            }
        });
    };
    /**
     * Creates a copy of an entity.
     * @param item The entity to copy.
     * @returns The copy.
     * @memberOf BreezeService
     */
    BreezeService.prototype.copy = function (item, deep) {
        if (deep === void 0) { deep = true; }
        if (!item) {
            return null;
        }
        var values = {};
        item.entityType.dataProperties.forEach(function (property) {
            if (!property.isPartOfKey) {
                if (!property.relatedNavigationProperty) {
                    values[property.name] = item[property.name];
                }
                else if (!property.isNullable) {
                    // Required relation
                    values[property.name] = item[property.name];
                }
            }
        });
        var result = item.entityType.createEntity(values);
        this.addEntity(result);
        return result;
    };
    /**
     * Prepares an entity for deletion. Sets the delete flag and decouples relations.
     * @param item The entity to prepare for deletion.
     * @memberOf BreezeService
     */
    BreezeService.prototype.setDeleted = function (item) {
        if (!item ||
            !item.entityAspect ||
            item.entityAspect.entityState === EntityState.Detached ||
            item.entityAspect.entityState === EntityState.Deleted) {
            return;
        }
        // Making automatic cascading delete possible for required associations
        item.entityType.navigationProperties.forEach(function (navigation) {
            if (!navigation.isScalar && navigation.inverse !== null) {
                var collection = item[navigation.name];
                var j = collection.length;
                if (navigation.inverse.relatedDataProperties[0].isNullable) {
                    /// Optional relation
                    while (j > 0) {
                        j -= 1;
                        collection.splice(j, 1);
                    }
                }
                else {
                    //// Required relation
                    while (j > 0) {
                        j -= 1;
                        collection[j].entityAspect.setDetached();
                        collection.splice(j, 1);
                    }
                }
            }
        });
        item.entityAspect.setDeleted();
    };
    /**
     * Gets an entity by key.
     * @param entityType The entity type to look for.
     * @param key The entity key to match.
     * @param checkLocalCacheFirst Whether to look in the local cache first. Default value is true.
     * @returns An AsyncSubject object to retrieve the requested entity.
     * @memberOf BreezeService
     */
    BreezeService.prototype.getEntityByKey = function (entityType, key, checkLocalCacheFirst, expand) {
        var _this = this;
        if (checkLocalCacheFirst === void 0) { checkLocalCacheFirst = true; }
        var result = new AsyncSubject();
        this.entityManager.fetchEntityByKey(entityType.name, key, checkLocalCacheFirst).then(function (value) {
            result.next(value.entity);
            result.complete();
            if (expand && value.entity != null) {
                var paths = expand.split(',');
                paths.forEach(function (path) {
                    _this.expandWith(value.entity, path, checkLocalCacheFirst);
                });
            }
        }).catch(function (reason) {
            result.error(reason);
            console.warn('Error BreezeService.getEntityByKey for ' + entityType.shortName);
            if (reason && reason.message) {
                console.warn(reason.message);
            }
        });
        return result;
    };
    /**
     * Loads and expands the navigation properties of the given path.
     * @public
     * @param {Entity} entity The entity with the property.
     * @param {string} path The comma separated path to expand.
     * @param {boolean} [checkLocalCacheFirst=true] If true expands only when not loaded.
     * @memberOf BreezeService
     */
    BreezeService.prototype.expandWith = function (entity, path, checkLocalCacheFirst, callback) {
        var _this = this;
        if (checkLocalCacheFirst === void 0) { checkLocalCacheFirst = true; }
        if (callback === void 0) { callback = function () { }; }
        if (entity == null || entity.entityAspect.entityState.isAdded()) {
            callback();
            return;
        }
        var segments = path.trim().split('.');
        if (segments.length > 0) {
            var propName_1 = segments[0];
            segments.splice(0, 1);
            var property = entity.entityType.navigationProperties.find(function (prop) {
                return prop.name === propName_1;
            });
            if (property && property.isNavigationProperty) {
                if (checkLocalCacheFirst && entity.entityAspect.isNavigationPropertyLoaded(property)) {
                    if (segments.length === 0) {
                        callback();
                    }
                    else {
                        var next_1 = '';
                        segments.forEach(function (segment) { next_1 += '.' + segment; });
                        next_1 = next_1.substring(1);
                        if (property.isScalar) {
                            this.expandWith(entity[property.name], next_1, checkLocalCacheFirst, callback);
                        }
                        else {
                            var arr = entity[property.name];
                            var count_1 = arr.length;
                            arr.forEach(function (element) {
                                _this.expandWith(element, next_1, checkLocalCacheFirst, function () {
                                    count_1--;
                                    if (count_1 <= 0) {
                                        callback();
                                    }
                                });
                            });
                        }
                    }
                    return;
                }
                entity.entityAspect.loadNavigationProperty(property, function (response) {
                    if (segments.length > 0) {
                        var next_2 = '';
                        segments.forEach(function (segment) { next_2 += '.' + segment; });
                        next_2 = next_2.substring(1);
                        response.results.forEach(function (item) {
                            _this.expandWith(item, next_2, checkLocalCacheFirst, callback);
                        });
                        return;
                    }
                    callback();
                });
                return;
            }
        }
        callback();
    };
    /**
     * Adds an entity to the local cache of the EntityManager.
     * @param entity The entity to add. Normally a new created one.
     * @returns The added entity.
     * @memberOf BreezeService
     */
    BreezeService.prototype.addEntity = function (entity) {
        return this.entityManager.addEntity(entity);
    };
    /**
     * Gets the EntityType by name.
     * @param entityType The name of the entity.
     * @returns An AsyncSubject object to retrieve the requested EntityType.
     * @memberOf BreezeService
     */
    BreezeService.prototype.getEntityType = function (entityType) {
        var entity = this.entityManager.metadataStore.getEntityType(entityType, false);
        if (!entity.defaultResourceName) {
            entity.setProperties({
                defaultResourceName: entity.shortName + 'Items'
            });
        }
        return entity;
    };
    /**
     * Loads the requested entity data of a DataComponent.
     * @param component The DataComponent that is requesting the entity data.
     * @param items The target collection for the retrieved entities.
     * @returns The asynchronous processor of the data retrieval.
     * @memberOf BreezeService
     */
    BreezeService.prototype.getDataSet = function (component, items) {
        var _this = this;
        var completer = new AsyncSubject();
        if (this.entityManager !== null) {
            this.loadDataSet(this.entityManager, completer, component, items);
            return completer.toPromise();
        }
        this.subscriptionPool.add(this.loading.subscribe({
            next: function (manager) {
                _this.loadDataSet(manager, completer, component, items);
            },
            error: function (reason) {
                completer.error(reason);
                console.warn('Error getDataSet (' + component.entityType.shortName + '): ' + reason);
            }
        }));
        return completer.toPromise();
    };
    /**
     * Returns wether or not the entitymanager contains added entities to commit.
     * if no entityManager is defined, it will return false
     * @returns {boolean} True when there are added entities to commit.
     * @memberOf BreezeService
     */
    BreezeService.prototype.hasAdded = function () {
        if (this.entityManager) {
            if (this.entityManager.hasChanges()) {
                var changes = this.entityManager.getChanges();
                var added = changes.filter(function (item) { return item.entityAspect.entityState.isAdded(); });
                return added.length > 0;
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    };
    /**
     * Returns wether or not the entitymanager contains changes.
     * If no entityManager is defined, it will return false.
     * @memberOf BreezeService
     */
    BreezeService.prototype.hasChanges = function (skipDeleted) {
        if (skipDeleted === void 0) { skipDeleted = false; }
        if (this.entityManager) {
            if (this.entityManager.hasChanges()) {
                var changes = this.entityManager.getChanges();
                // DO NOT ADD!: const added = changes.filter(item => item.entityAspect.entityState.isAdded());
                var deleted = skipDeleted ? [] : changes.filter(function (item) { return item.entityAspect.entityState.isDeleted(); });
                var modified = changes.filter(function (item) { return item.entityAspect.entityState.isModified(); });
                // Soms kan ie dirty worden en deleted teld maar 1 keer.
                var dirty = changes.filter(function (item) { return item[DIRTY_FLAG] === true
                    && !item.entityAspect.entityState.isDeleted()
                    && !item.entityAspect.entityState.isAdded(); });
                // An entity in isAdded state can NOT go in isModified state before the commit.
                // An entity in isAdded state can go in a "Dirty === true" state.
                // This is an important difference between Dirty and isModified state.
                /*
                console.log(
                    'Changes: DIRTY: ' + dirty.length +
                    ' DELETED: ' + deleted.length +
                    ' MODIFIED: ' + modified.length
                );
                */
                return (dirty.length > 0 || deleted.length > 0 || modified.length > 0);
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    };
    /**
     * Gets the master entity of a navigation property from the local cache.
     * Sometimes breeze fails to link navigation properties.
     * This offers a work-around.
     * @param {Entity} entity The detail entity of the relation.
     * @param {string} propertyName The name of the navigation property.
     * @param {string} [masterType] The master type name. Sometimes needed for abstract navigation property types.
     * @returns {Entity} The found master otherwise null.
     * @memberOf BreezeService
     * */
    BreezeService.prototype.getNavigationPropertyLocally = function (entity, propertyName, masterType) {
        if (!entity) {
            return null;
        }
        var result = entity[propertyName];
        if (result) {
            return result;
        }
        var property = entity.entityType.getNavigationProperty(propertyName);
        if (!property) {
            return null;
        }
        if (property.foreignKeyNames.length !== 1) {
            return null;
        }
        var valueType = property.entityType;
        if (masterType) {
            try {
                valueType = entity.entityAspect.entityManager.metadataStore.getEntityType(masterType, false);
            }
            catch (error) {
                console.warn('Unknown entity type: ' + masterType);
                throw error;
            }
        }
        if (valueType.keyProperties.length !== 1) {
            return null;
        }
        var sourceName = valueType.defaultResourceName === '' || valueType.defaultResourceName == null ? valueType.shortName + 'Items' : valueType.defaultResourceName;
        var entityQuery = EntityQuery.from(sourceName).where(valueType.keyProperties[0].name, 'equals', entity[property.foreignKeyNames[0]]);
        var data = entity.entityAspect.entityManager.executeQueryLocally(entityQuery);
        if (data.length !== 1) {
            return null;
        }
        return data[0];
    };
    /**
     * Returns related entities of the main entity.
     * @param {Entity} entity Relations of this entity are returned.
     * @param {string} relationName The name of the entity type of the related entities.
     * @param {string} [navigationName] The navigation property name on the related entities that refers to the main entity.
     * @returns {Entity[]} An array of related entities.
     * @memberOf BreezeService
     */
    BreezeService.prototype.getRelationsLocally = function (entity, relationName, navigationName) {
        if (!entity) {
            return null;
        }
        var relationType = entity.entityAspect.entityManager.metadataStore.getEntityType(relationName, false);
        if (!relationType) {
            return null;
        }
        var navigationProperty = relationType.getNavigationProperty(navigationName || entity.entityType.shortName);
        if (!navigationProperty) {
            return null;
        }
        var sourceName = relationType.defaultResourceName === '' || relationType.defaultResourceName == null ? relationType.shortName + 'Items' : relationType.defaultResourceName;
        var entityQuery = EntityQuery.from(sourceName).where(navigationProperty.foreignKeyNames[0], 'equals', entity[entity.entityType.keyProperties[0].name]);
        var data = entity.entityAspect.entityManager.executeQueryLocally(entityQuery);
        return data;
    };
    /**
     * Rejects current changes, emptying the changeset and restoring entities to their previous state
     */
    BreezeService.prototype.rejectChanges = function () {
        if (!this.entityManager) {
            return;
        }
        if (this.dataContextSet.length === 0) {
            this.entityManager.rejectChanges();
            return;
        }
        var dataContext = this.dataContextSet[this.dataContextSet.length - 1];
        dataContext.rejectChanges();
    };
    /**
     * Sets default values for changed entities (just before saving).
     * Specially for standard entity types.
     * @param item The entity to update.
     * @memberOf BreezeService
     */
    BreezeService.prototype.setDefaultValuesBeforeSave = function (saveResult) {
    };
    /**
     * Save the entity changes currently known within the EntityManager
     * @param callback Callback function for communicating errors while saving.
     * @returns The asyncore processor of the data storing.
     * @memberOf BreezeService
     */
    BreezeService.prototype.saveChanges = function (callback, configuration, context) {
        var _this = this;
        if (callback === void 0) { callback = function (error) {
            console.warn('SaveChanges without callback is an anti-pattern');
        }; }
        if (configuration === void 0) { configuration = null; }
        if (context === void 0) { context = null; }
        var result = new AsyncSubject();
        this.validateBeforeSave(function (errors) {
            if (errors) {
                callback(errors);
                result.complete();
                return;
            }
            _this.setDefaultValuesBeforeSave(result);
            if (_this._savingInProgress) {
                _this.subscriptionPool.add(_this._savingInProgress
                    .subscribe({
                    complete: function () {
                        _this.processChanges(result, callback, context);
                    },
                    error: function () {
                        _this.processChanges(result, callback, context);
                    }
                }));
                return result.asObservable();
            }
            _this.processChanges(result, callback, context);
        }, configuration);
        return result.asObservable();
    };
    /**
     * Translates and shows entity errors received in server response
     * @param {EntityError[]} errors The entity errors received from server
     * @param {EntityConfiguration} configuration The entity configuration
     * @returns {any[]} Validation errors
     */
    BreezeService.prototype.publishServerEntityErrors = function (errors, configuration) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var validationErrors, _loop_1, this_1, _i, errors_1, err, state_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        validationErrors = [];
                        _loop_1 = function (err) {
                            var entityType, propertyName, invalidLocationValue, fieldLabel, entityLabel, errorField, labels, translation, newEntityLabel, newFieldLabel, details, labelKey;
                            return tslib_1.__generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        entityType = err.entity.entityType.shortName.toUpperCase();
                                        propertyName = err.propertyName;
                                        if (!(entityType === 'LOCATION' && err.entity.entityAspect.entityState.isAdded() && propertyName === 'Name' && err.errorName === 'required')) return [3 /*break*/, 2];
                                        return [4 /*yield*/, this_1.translateService.get('APP.DOMAIN.VALIDATORS.INVALID_LOCATION_CREATED').toPromise()];
                                    case 1:
                                        invalidLocationValue = _a.sent();
                                        if (invalidLocationValue) {
                                            validationErrors.push(invalidLocationValue);
                                            this_1.alertsService.createAlert('warning', invalidLocationValue);
                                        }
                                        return [2 /*return*/, { value: void 0 }];
                                    case 2:
                                        fieldLabel = "APP.MISC." + entityType + "." + propertyName.toUpperCase();
                                        entityLabel = "APP.MISC." + entityType + ".#SINGULAR";
                                        if (configuration) {
                                            errorField = configuration.fields.find(function (field) {
                                                return field.name === propertyName;
                                            });
                                            if (errorField) {
                                                fieldLabel = errorField.labelKey ? errorField.labelKey : fieldLabel;
                                            }
                                        }
                                        labels = fieldLabel ? [entityLabel, fieldLabel] : [entityLabel];
                                        return [4 /*yield*/, this_1.translateService.get(labels).toPromise()];
                                    case 3:
                                        translation = _a.sent();
                                        newEntityLabel = translation[entityLabel] === entityLabel ? err.entity.entityType.shortName : translation[entityLabel];
                                        newFieldLabel = translation[fieldLabel] === fieldLabel ? propertyName : translation[fieldLabel];
                                        details = [newEntityLabel, newFieldLabel];
                                        return [4 /*yield*/, this_1.translateService.get(err.errorName).toPromise()];
                                    case 4:
                                        labelKey = _a.sent();
                                        if (labelKey === err.errorName) {
                                            validationErrors.push(err.errorMessage ? err.errorMessage : "Error " + err.errorName + " " + err.entity.entityType.shortName + "." + propertyName);
                                            this_1.alertsService.createAlert('warning', err.errorMessage, details);
                                        }
                                        else {
                                            validationErrors.push(labelKey + " (" + newEntityLabel + "." + newFieldLabel + ")");
                                            this_1.alertsService.createAlert('warning', labelKey, details);
                                        }
                                        return [2 /*return*/];
                                }
                            });
                        };
                        this_1 = this;
                        _i = 0, errors_1 = errors;
                        _a.label = 1;
                    case 1:
                        if (!(_i < errors_1.length)) return [3 /*break*/, 4];
                        err = errors_1[_i];
                        return [5 /*yield**/, _loop_1(err)];
                    case 2:
                        state_1 = _a.sent();
                        if (typeof state_1 === "object")
                            return [2 /*return*/, state_1.value];
                        _a.label = 3;
                    case 3:
                        _i++;
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/, validationErrors];
                }
            });
        });
    };
    /**
     * Virtual method to setup a new master detail relation.
     * @param masterType The entity type of the master.
     * @param masterOid The object identifier of the master.
     * @param detail The detail instance to setup the relation with.
     * @returns The entity that links the master with the detail.
     */
    BreezeService.prototype.setupMasterDetail = function (masterType, masterOid, detail) {
        return null;
    };
    /**
     * Virtual entity validation method.
     * @param {EntityConfiguration} configuration The configuration contains the entity fields.
     * @returns {Observable<Array<string>>} An array of validation errors.
     * @memberOf BreezeService
     */
    BreezeService.prototype.validateBeforeSave = function (callback, configuration) {
        callback();
    };
    /**
     * Builds the query defined by the DataComponent.
     * @param component The DataComponent
     * @returns The created EntityQuery
     */
    BreezeService.prototype.buildQuery = function (component) {
        var source = component.sourceName ? component.sourceName : component.entityType.defaultResourceName;
        var entityQuery = EntityQuery.from(source);
        if (component.queryOptions && component.queryOptions.parameters) {
            entityQuery = entityQuery.withParameters(component.queryOptions.parameters);
        }
        if (component.filter) {
            var predicate = component.filter.createPredicate(component.entityType);
            if (predicate !== null) {
                entityQuery = entityQuery.where(predicate);
            }
        }
        var sortList = [];
        component.sorting.forEach(function (item) {
            if (item.direction !== SortDirection.None) {
                sortList.push(item);
            }
        });
        if (sortList.length > 0) {
            sortList = sortList.sort(function (a, b) {
                return a.order - b.order;
            });
            var sort_1 = '';
            sortList.forEach(function (descriptor) {
                // Support sorting for multi field composed list columns.
                var fields = (descriptor.property + '').split(',');
                fields.map(function (field) { return field.trim(); }).forEach(function (field) {
                    if (field.length > 0) {
                        sort_1 += ',' + field;
                        sort_1 += descriptor.direction === SortDirection.Descending ? ' desc' : '';
                    }
                });
            });
            sort_1 = sort_1.substring(1);
            if (sort_1.length > 0) {
                entityQuery = entityQuery.orderBy(sort_1);
            }
        }
        if (!component.queryOptions) {
            return entityQuery;
        }
        if (component.queryOptions.expand) {
            entityQuery = entityQuery.expand(component.queryOptions.expand);
        }
        if (component.queryOptions.skip) {
            entityQuery = entityQuery.skip(component.queryOptions.skip);
        }
        if (component.queryOptions.take) {
            entityQuery = entityQuery.take(component.queryOptions.take);
        }
        if (component.queryOptions.take || component.queryOptions.skip) {
            entityQuery = entityQuery.inlineCount();
        }
        return entityQuery;
    };
    /**
     * Loads the requested entity data of a DataComponent.
     * @param manager The EntityManager.
     * @param completer Event handler for notification of a successful data retrieval.
     * @param component The DataComponent with the data request.
     * @param checklist The target collection for the retrieved entities.
     * @memberOf BreezeService
     */
    BreezeService.prototype.loadDataSet = function (manager, completer, component, items) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var entityQuery, waiting, data, waitAlert, reason_1, waitAlert, error_1;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._isLoading++;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, , 7]);
                        entityQuery = this.buildQuery(component);
                        waiting = window.setTimeout(function () {
                            var waitAlert = _this.alertsService.alerts.find(function (alert) {
                                return alert.labelKey === LOADING_ALERT;
                            });
                            if (waitAlert) {
                                // Already a loading alert scheduled.
                                return;
                            }
                            _this.alertsService.createAlert('info', LOADING_ALERT);
                            waitAlert = _this.alertsService.alerts.find(function (alert) {
                                return alert.labelKey === LOADING_ALERT;
                            });
                            _this.alertsService.stopAutoDelete(waitAlert.id);
                        }, 5000);
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, manager.executeQuery(entityQuery)];
                    case 3:
                        data = _a.sent();
                        window.clearTimeout(waiting);
                        component.inlineCount = data.inlineCount;
                        if (items) {
                            items.length = 0;
                            data.results.forEach(function (item) {
                                items.push(item);
                            });
                        }
                        if (items && !component.inlineCount) {
                            component.inlineCount = items.length;
                        }
                        completer.next(data);
                        completer.complete(); // To force notification to subscribers.
                        this.queryFinishedEvent.emit(data);
                        this._isLoading--;
                        waitAlert = this.alertsService.alerts.find(function (alert) {
                            return alert.labelKey === LOADING_ALERT;
                        });
                        if (waitAlert) {
                            this.alertsService.deleteAlert(waitAlert.id);
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        reason_1 = _a.sent();
                        window.clearTimeout(waiting);
                        waitAlert = this.alertsService.alerts.find(function (alert) {
                            return alert.labelKey === LOADING_ALERT;
                        });
                        if (waitAlert) {
                            this.alertsService.deleteAlert(waitAlert.id);
                        }
                        this._isLoading--;
                        completer.error(reason_1);
                        completer.complete(); // Was completer.error(reason); zie issue #1844
                        return [3 /*break*/, 5];
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        error_1 = _a.sent();
                        this._isLoading = 0;
                        throw error_1;
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Is called sequencial to avoid concurrent saves.
     * @param event The event that will process the changes.
     * @param callback The callback function to enable progress feedback.
     * @memberOf BreezeService
     */
    BreezeService.prototype.processChanges = function (event, callback, context) {
        var _this = this;
        if (this.entityManager === null) {
            callback('The domain service is not available');
            event.error(new Error('Domain service probably offline.'));
            return;
        }
        var self = this;
        this._savingInProgress = event;
        var invoked = {};
        invoked[INVOKER_AFTER_SAVE] = [];
        invoked[INVOKER_BEFORE_SAVE] = [];
        var invokeRemote = {};
        invokeRemote[INVOKER_AFTER_SAVE] = [];
        invokeRemote[INVOKER_BEFORE_SAVE] = [];
        var changes = this.entityManager.getChanges();
        changes.forEach(function (item) {
            var afterSave = item[INVOKER_AFTER_SAVE];
            if (afterSave) {
                var invoker_1 = {};
                Object.keys(afterSave).forEach(function (name) {
                    invoker_1[name] = { parameters: afterSave[name]['parameters'] };
                });
                invokeRemote[INVOKER_AFTER_SAVE].push({
                    'entity': item.entityType.shortName,
                    'key': item.entityAspect.getKey().values[0],
                    'invoke': invoker_1
                });
                invoked[INVOKER_AFTER_SAVE].push(item);
            }
            var beforeSave = item[INVOKER_BEFORE_SAVE];
            if (beforeSave) {
                var invoker_2 = {};
                Object.keys(beforeSave).forEach(function (name) {
                    invoker_2[name] = { parameters: beforeSave[name]['parameters'] };
                });
                invokeRemote[INVOKER_BEFORE_SAVE].push({
                    'entity': item.entityType.shortName,
                    'key': item.entityAspect.getKey().values[0],
                    'invoke': invoker_2
                });
                invoked[INVOKER_BEFORE_SAVE].push(item);
            }
            if (item.entityAspect.originalValues) {
                item.entityType.unmappedProperties.forEach(function (unmapped) {
                    if (item.entityAspect.originalValues[unmapped.name] != null) {
                        // To avoid error of issue #2156: Converting circular structure to JSON
                        // See breeze.debug.js: proto.saveChanges = function (saveContext, saveBundle)
                        // with code line: var bundle = JSON.stringify(saveBundle);
                        delete item.entityAspect.originalValues[unmapped.name];
                    }
                });
            }
        });
        var saveOptions = new SaveOptions();
        var invoking = invokeRemote[INVOKER_AFTER_SAVE].length > 0 || invokeRemote[INVOKER_BEFORE_SAVE].length > 0;
        if (context != null || invoking) {
            var options = {};
            if (invoking) {
                options = invokeRemote;
            }
            if (context != null) {
                options['@context'] = context;
            }
            saveOptions = new SaveOptions({ tag: options });
        }
        this.entityManager.saveChanges(null, saveOptions).then(function (value) {
            var actions = [];
            _this.clearInvocation(invoked, INVOKER_BEFORE_SAVE, actions);
            _this.clearInvocation(invoked, INVOKER_AFTER_SAVE, actions);
            callback();
            event.next(value);
            event.complete();
            self._savingInProgress = null;
            _this.commitFinishedEvent.emit(value);
            _this.completeInvocation(actions);
        }).catch(function (value) {
            var actions = [];
            _this.clearInvocation(invoked, INVOKER_BEFORE_SAVE, actions);
            _this.clearInvocation(invoked, INVOKER_AFTER_SAVE, actions);
            callback(value);
            event.complete();
            _this.completeInvocation(actions);
            if (_this.entityManager.hasChanges()) {
                // Fix: Onterechte 'Er zijn wijzigingen' overlay bij pogingen tot verwijderen van afwezigheden #2607
                var stillChanged = _this.entityManager.getChanges();
                if (stillChanged.find(function (it) { return !it.entityAspect.entityState.isDeleted(); }) == null) {
                    // Alleen deleted in changeSet.
                    // We kunnen er ook voor kiezen om heel de changeSet met deleted terug te rollen.
                    // Ik kies er nu voor alleen dit te doen als het een delete entity error is.
                    var errors = value.entityErrors;
                    if (errors) {
                        // Entity errors voor deleted terug rollen.
                        errors.filter(function (it) { return it.entity.entityAspect.entityState.isDeleted(); }).forEach(function (it) {
                            it.entity.entityAspect.rejectChanges();
                        });
                    }
                }
            }
            // To make error silent.
            self._savingInProgress = null;
        });
    };
    /**
     * Completes the invocation by singling the end.
     * @private
     * @param {Array<any>} actions Array with actions to complete
     * @memberOf BreezeService
     */
    BreezeService.prototype.completeInvocation = function (actions) {
        actions.forEach(function (item) {
            var invoker = item['invoker'];
            Object.keys(invoker).forEach(function (name) {
                var process = invoker[name]['process'];
                if (process) {
                    process.emit(item['entity']);
                }
            });
        });
    };
    /**
     * Clears invocation information from entity.
     * @private
     * @param {*} invoked Invoked entity.
     * @param {string} invokeType The ivoke type to clear.
     * @param {Array<any>} result Resulting array with actions.
     * @memberOf BreezeService
     */
    BreezeService.prototype.clearInvocation = function (invoked, invokeType, result) {
        invoked[invokeType].forEach(function (item) {
            var invoker = item[invokeType];
            if (invoker) {
                result.push({ entity: item, invoker: invoker });
                delete item[invokeType];
            }
        });
    };
    /**
     * Emits the found entity type to a asyncore event.
     * @param entityType The short name of the entity type.
     * @param event The asyncore event to emit.
     * @memberOf BreezeService
     */
    BreezeService.prototype.emitEntityType = function (entityType, event) {
        try {
            var entity = this.entityManager.metadataStore.getEntityType(entityType, false);
            if (!entity.defaultResourceName) {
                entity.setProperties({
                    defaultResourceName: entity.shortName + 'Items'
                });
            }
            event.next(entity);
            event.complete();
        }
        catch (reason) {
            event.error(reason);
        }
    };
    /**
    * Method to register entity validators to check property rules client side.
    * @param entityType Name of the entity
    * @param property Name of property that the validator validates
    * @param validator Function or array of functions that validates the property.
    * @memberOf DomainService
    */
    BreezeService.prototype.registerValidator = function (entityType, property, validator) {
        var _this = this;
        if (Array.isArray(validator)) {
            validator.forEach(function (item) {
                _this.registerValidator(entityType, property, item);
            });
            return;
        }
        var vldtr = validator;
        if (typeof vldtr === 'string') {
            vldtr = config.functionRegistry['Validator.' + vldtr]();
        }
        var type = this.getEntityType(entityType);
        type.getProperty(property, true).validators.push(vldtr);
    };
    return BreezeService;
}(AbstractBase));
export { BreezeService };
